/**
 * Project List Colours settings tab — SPEC-project-list-colour-rules / R3.
 *
 * Self-serve editor for the per-tenant colouring rules consumed by the R2
 * engine. Lists the current rules (name, slot, colour swatch, enabled, order),
 * supports add / edit / delete / toggle / reorder, and persists the WHOLE
 * config via the R1 PUT endpoint (full replace — we always send every rule).
 *
 * Patterns mirror ProjectCategoriesTab / BrandingSettingsTab: a list + a
 * Dialog-based editor with local form state, Radix Card/Button/Input/Label +
 * Tailwind + lucide icons. Colours come ONLY from R2's curated PALETTE — there
 * is no free-form hex entry anywhere.
 */
import { useEffect, useMemo, useState } from 'react';
import {
  PALETTE,
  paletteClass,
  type ColorRule,
  type ColorSlot,
  type Condition,
  type ConditionField,
  type ConditionOperator,
  type PaletteColor,
  type ProjectListColorsConfig,
} from '@/lib/projectListColors';
import {
  useProjectListColors,
  useUpdateProjectListColors,
} from '@/hooks/useSettings';
import { useProjectCategoryOptions } from '@/hooks';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Plus,
  Pencil,
  Trash2,
  ChevronUp,
  ChevronDown,
  Sparkles,
  Check,
} from 'lucide-react';
import { toast } from 'sonner';

// ── static vocab (mirrors R1/R2 wire contract) ──────────────────────────────

const SLOT_OPTIONS: { value: ColorSlot; label: string }[] = [
  { value: 'border_left', label: 'Left border' },
  { value: 'dot', label: 'Dot' },
  { value: 'badge', label: 'Badge' },
  { value: 'row_tint', label: 'Row tint' },
];

const SLOT_LABELS: Record<ColorSlot, string> = {
  border_left: 'Left border',
  dot: 'Dot',
  badge: 'Badge',
  row_tint: 'Row tint',
};

const FIELD_OPTIONS: { value: ConditionField; label: string }[] = [
  { value: 'category', label: 'Category' },
  { value: 'effective_fee', label: 'Effective fee' },
  { value: 'workflow_stage', label: 'Workflow stage' },
];

const OPERATOR_OPTIONS: { value: ConditionOperator; label: string }[] = [
  { value: 'equals', label: 'equals' },
  { value: 'in', label: 'is one of' },
  { value: 'lt', label: 'less than' },
  { value: 'lte', label: 'less than or equal' },
  { value: 'gt', label: 'greater than' },
  { value: 'gte', label: 'greater than or equal' },
  { value: 'between', label: 'between' },
];

/**
 * Which operators make sense for each field. Category / workflow_stage are
 * discrete (equals / in); effective_fee is numeric (comparisons + between).
 */
const OPERATORS_BY_FIELD: Record<ConditionField, ConditionOperator[]> = {
  category: ['equals', 'in'],
  workflow_stage: ['equals', 'in'],
  effective_fee: ['lt', 'lte', 'gt', 'gte', 'between'],
};

// Canonical workflow stage list — reused from WorkflowStageSelect's vocab so the
// dropdown matches what GET /api/projects actually returns.
const WORKFLOW_STAGE_OPTIONS: { value: string; label: string }[] = [
  { value: 'proposal_draft', label: 'Proposal: Draft' },
  { value: 'proposal_sent', label: 'Proposal: Sent' },
  { value: 'proposal_declined', label: 'Proposal: Declined' },
  { value: 'project_setup', label: 'Project Setup' },
  { value: 'design_development', label: 'Design Development' },
  { value: 'permit_preparation', label: 'Permit Preparation' },
  { value: 'in_permit_review', label: 'In Permit Review' },
  { value: 'construction_administration', label: 'Construction Administration' },
  { value: 'on_hold', label: 'On Hold' },
  { value: 'complete', label: 'Complete' },
];

// Palette swatch order for pickers + the "colour per category" rotation.
const PALETTE_COLORS: PaletteColor[] = [
  'blue',
  'amber',
  'emerald',
  'teal',
  'violet',
  'rose',
  'slate',
  'indigo',
];

const CONFIG_VERSION = 1;

// ── helpers ──────────────────────────────────────────────────────────────────

let _idCounter = 0;
/** Stable-enough client id for a new rule (server persists it verbatim). */
function newRuleId(): string {
  _idCounter += 1;
  return `rule_${Date.now().toString(36)}_${_idCounter}`;
}

function defaultCondition(field: ConditionField = 'category'): Condition {
  return { field, operator: OPERATORS_BY_FIELD[field][0], value: '' };
}

function emptyRule(displayOrder: number): ColorRule {
  return {
    id: newRuleId(),
    name: '',
    enabled: true,
    slot: 'dot',
    conditions: [defaultCondition('category')],
    color: 'blue',
    display_order: displayOrder,
  };
}

/** A small inline ● + label swatch preview for a {color, slot} pair. */
function SwatchPreview({ color, slot }: { color: PaletteColor; slot: ColorSlot }) {
  if (slot === 'badge') {
    return (
      <span
        className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${paletteClass(
          color,
          'badge',
        )}`}
        data-testid={`swatch-preview-${color}`}
      >
        {color}
      </span>
    );
  }
  if (slot === 'row_tint') {
    return (
      <span
        className={`inline-flex items-center rounded px-2 py-0.5 text-xs ${paletteClass(
          color,
          'row_tint',
        )}`}
        data-testid={`swatch-preview-${color}`}
      >
        {color}
      </span>
    );
  }
  // border_left + dot both read as a coloured glyph.
  return (
    <span
      className="inline-flex items-center gap-1 text-xs"
      data-testid={`swatch-preview-${color}`}
    >
      <span className={`text-base leading-none ${paletteClass(color, 'dot')}`}>●</span>
      {color}
    </span>
  );
}

// ── component ────────────────────────────────────────────────────────────────

export function ProjectListColorsTab() {
  const { data: config, isLoading } = useProjectListColors();
  const { data: categories } = useProjectCategoryOptions();
  const updateColors = useUpdateProjectListColors();

  // Local working copy of the rules. Seeded from the fetched config; the user
  // edits this and Save sends the FULL array.
  const [rules, setRules] = useState<ColorRule[]>([]);

  useEffect(() => {
    if (config?.rules) {
      setRules(config.rules.map((r) => ({ ...r })));
    }
  }, [config]);

  // Editor dialog state.
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [draft, setDraft] = useState<ColorRule | null>(null);

  const activeCategories = useMemo(
    () => (categories ?? []).filter((c) => c.is_active),
    [categories],
  );

  const sortedRules = useMemo(
    () => [...rules].sort((a, b) => a.display_order - b.display_order),
    [rules],
  );

  // ── list actions ──────────────────────────────────────────────────────────

  const openCreate = () => {
    const nextOrder =
      rules.length === 0 ? 0 : Math.max(...rules.map((r) => r.display_order)) + 1;
    setDraft(emptyRule(nextOrder));
    setIsDialogOpen(true);
  };

  const openEdit = (rule: ColorRule) => {
    // Deep-ish clone so editing the draft doesn't mutate list state until Save.
    setDraft({ ...rule, conditions: rule.conditions.map((c) => ({ ...c })) });
    setIsDialogOpen(true);
  };

  const closeDialog = () => {
    setIsDialogOpen(false);
    setDraft(null);
  };

  const deleteRule = (id: string) => {
    setRules((prev) => prev.filter((r) => r.id !== id));
  };

  const toggleEnabled = (id: string) => {
    setRules((prev) =>
      prev.map((r) => (r.id === id ? { ...r, enabled: !r.enabled } : r)),
    );
  };

  /** Swap display_order with the adjacent rule in sorted order. */
  const move = (id: string, dir: 'up' | 'down') => {
    const ordered = [...rules].sort((a, b) => a.display_order - b.display_order);
    const idx = ordered.findIndex((r) => r.id === id);
    if (idx === -1) return;
    const swapWith = dir === 'up' ? idx - 1 : idx + 1;
    if (swapWith < 0 || swapWith >= ordered.length) return;
    const a = ordered[idx];
    const b = ordered[swapWith];
    const aOrder = a.display_order;
    const bOrder = b.display_order;
    setRules((prev) =>
      prev.map((r) => {
        if (r.id === a.id) return { ...r, display_order: bOrder };
        if (r.id === b.id) return { ...r, display_order: aOrder };
        return r;
      }),
    );
  };

  const saveDraft = () => {
    if (!draft) return;
    if (!draft.name.trim()) {
      toast.error('Rule name is required');
      return;
    }
    const cleaned: ColorRule = { ...draft, name: draft.name.trim() };
    setRules((prev) => {
      const exists = prev.some((r) => r.id === cleaned.id);
      return exists
        ? prev.map((r) => (r.id === cleaned.id ? cleaned : r))
        : [...prev, cleaned];
    });
    closeDialog();
  };

  /**
   * Convenience: append one dot-slot rule per ACTIVE category, each pre-filled
   * with a DISTINCT palette colour (rotating through the 8). Jamie's per-category
   * dots in one click — the user can then tweak/remove individual rules.
   */
  const addColourPerCategory = () => {
    if (activeCategories.length === 0) {
      toast.error('No active categories to colour');
      return;
    }
    setRules((prev) => {
      let order =
        prev.length === 0 ? 0 : Math.max(...prev.map((r) => r.display_order)) + 1;
      const additions: ColorRule[] = activeCategories.map((cat, i) => {
        const rule: ColorRule = {
          id: newRuleId(),
          name: `${cat.value} dot`,
          enabled: true,
          slot: 'dot',
          conditions: [{ field: 'category', operator: 'equals', value: cat.value }],
          color: PALETTE_COLORS[i % PALETTE_COLORS.length],
          display_order: order,
        };
        order += 1;
        return rule;
      });
      return [...prev, ...additions];
    });
    toast.success(`Added ${activeCategories.length} category colour rules`);
  };

  // ── save (full replace) ───────────────────────────────────────────────────

  const handleSave = async () => {
    const payload: ProjectListColorsConfig = {
      version: config?.version ?? CONFIG_VERSION,
      // Always send the COMPLETE rules array (PUT is a full replace).
      rules: sortedRules.map((r, i) => ({ ...r, display_order: i })),
    };
    try {
      await updateColors.mutateAsync(payload);
    } catch {
      // toast handled in the hook
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-4" data-testid="project-list-colours-loading">
        {[...Array(3)].map((_, idx) => (
          <Skeleton key={idx} className="h-16 w-full" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-gray-900">Project List Colours</h2>
          <p className="text-sm text-gray-600">
            Colour-code project-list rows by conditions. Rules apply top-to-bottom; the
            first matching rule per slot wins.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Button
            variant="outline"
            onClick={addColourPerCategory}
            className="inline-flex items-center gap-2"
            data-testid="colour-per-category"
          >
            <Sparkles className="h-4 w-4" />
            Add a colour per category
          </Button>
          <Button onClick={openCreate} className="inline-flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Rule
          </Button>
        </div>
      </div>

      {sortedRules.length === 0 ? (
        <div className="rounded-lg border-2 border-dashed border-gray-300 p-8 text-center">
          <p className="font-medium text-gray-700">No colour rules yet</p>
          <p className="mt-1 text-sm text-muted-foreground">
            The project list renders normally until you add a rule.
          </p>
          <Button onClick={openCreate} className="mt-4 inline-flex items-center gap-2">
            <Plus className="h-4 w-4" />
            Add Rule
          </Button>
        </div>
      ) : (
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-24">Order</TableHead>
                <TableHead>Name</TableHead>
                <TableHead>Slot</TableHead>
                <TableHead>Colour</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedRules.map((rule, idx) => (
                <TableRow key={rule.id} data-testid={`rule-row-${rule.id}`}>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => move(rule.id, 'up')}
                        disabled={idx === 0}
                        aria-label={`Move ${rule.name} up`}
                      >
                        <ChevronUp className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => move(rule.id, 'down')}
                        disabled={idx === sortedRules.length - 1}
                        aria-label={`Move ${rule.name} down`}
                      >
                        <ChevronDown className="h-4 w-4" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell className="font-medium">{rule.name}</TableCell>
                  <TableCell>{SLOT_LABELS[rule.slot]}</TableCell>
                  <TableCell>
                    <SwatchPreview color={rule.color} slot={rule.slot} />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => toggleEnabled(rule.id)}
                      aria-label={`Toggle ${rule.name}`}
                      data-testid={`toggle-${rule.id}`}
                    >
                      {rule.enabled ? (
                        <Badge variant="secondary">Enabled</Badge>
                      ) : (
                        <Badge variant="outline">Disabled</Badge>
                      )}
                    </Button>
                  </TableCell>
                  <TableCell className="flex justify-end gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => openEdit(rule)}
                      className="inline-flex items-center gap-1.5"
                    >
                      <Pencil className="h-3.5 w-3.5" />
                      Edit
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => deleteRule(rule.id)}
                      className="inline-flex items-center gap-1.5 text-red-600 hover:bg-red-50"
                      aria-label={`Delete ${rule.name}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <div className="flex items-center justify-end gap-2">
        <Button
          onClick={handleSave}
          disabled={updateColors.isPending}
          data-testid="save-colours"
        >
          {updateColors.isPending ? 'Saving…' : 'Save Changes'}
        </Button>
      </div>

      {draft && (
        <RuleEditorDialog
          open={isDialogOpen}
          draft={draft}
          activeCategoryValues={activeCategories.map((c) => c.value)}
          onChange={setDraft}
          onCancel={closeDialog}
          onSave={saveDraft}
        />
      )}
    </div>
  );
}

// ── rule editor dialog ───────────────────────────────────────────────────────

interface RuleEditorDialogProps {
  open: boolean;
  draft: ColorRule;
  activeCategoryValues: string[];
  onChange: (rule: ColorRule) => void;
  onCancel: () => void;
  onSave: () => void;
}

function RuleEditorDialog({
  open,
  draft,
  activeCategoryValues,
  onChange,
  onCancel,
  onSave,
}: RuleEditorDialogProps) {
  const isEdit = draft.name.trim().length > 0;

  const setField = <K extends keyof ColorRule>(key: K, value: ColorRule[K]) => {
    onChange({ ...draft, [key]: value });
  };

  const setCondition = (index: number, next: Condition) => {
    onChange({
      ...draft,
      conditions: draft.conditions.map((c, i) => (i === index ? next : c)),
    });
  };

  const addCondition = () => {
    onChange({ ...draft, conditions: [...draft.conditions, defaultCondition('category')] });
  };

  const removeCondition = (index: number) => {
    onChange({
      ...draft,
      conditions: draft.conditions.filter((_, i) => i !== index),
    });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onCancel()}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit Rule' : 'Add Rule'}</DialogTitle>
        </DialogHeader>

        <div className="space-y-5 py-2">
          {/* Name */}
          <div className="space-y-2">
            <Label htmlFor="rule-name">Name</Label>
            <Input
              id="rule-name"
              value={draft.name}
              onChange={(e) => setField('name', e.target.value)}
              placeholder="e.g., Architectural under $50k"
            />
          </div>

          {/* Slot */}
          <div className="space-y-2">
            <Label htmlFor="rule-slot">Slot</Label>
            <select
              id="rule-slot"
              value={draft.slot}
              onChange={(e) => setField('slot', e.target.value as ColorSlot)}
              className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm"
            >
              {SLOT_OPTIONS.map((s) => (
                <option key={s.value} value={s.value}>
                  {s.label}
                </option>
              ))}
            </select>
          </div>

          {/* Conditions */}
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <Label>Conditions (all must match)</Label>
              <Button
                variant="outline"
                size="sm"
                onClick={addCondition}
                className="inline-flex items-center gap-1.5"
                data-testid="add-condition"
              >
                <Plus className="h-3.5 w-3.5" />
                Add condition
              </Button>
            </div>
            <div className="space-y-3">
              {draft.conditions.map((condition, index) => (
                <ConditionRow
                  key={index}
                  index={index}
                  condition={condition}
                  activeCategoryValues={activeCategoryValues}
                  canRemove={draft.conditions.length > 1}
                  onChange={(next) => setCondition(index, next)}
                  onRemove={() => removeCondition(index)}
                />
              ))}
            </div>
          </div>

          {/* Colour swatch picker */}
          <div className="space-y-2">
            <Label>Colour</Label>
            <div className="flex flex-wrap gap-2" data-testid="palette-swatches">
              {PALETTE_COLORS.map((color) => {
                const selected = draft.color === color;
                return (
                  <button
                    key={color}
                    type="button"
                    onClick={() => setField('color', color)}
                    aria-label={`Colour ${color}`}
                    aria-pressed={selected}
                    data-testid={`swatch-${color}`}
                    className={`relative h-9 w-9 rounded-full border-2 transition ${
                      selected ? 'border-gray-900 ring-2 ring-gray-300' : 'border-transparent'
                    } ${PALETTE[color].row_tint}`}
                    style={undefined}
                    title={color}
                  >
                    <span
                      className={`absolute inset-0 m-auto h-4 w-4 rounded-full ${paletteClass(
                        color,
                        'dot',
                      ).replace('text-', 'bg-')}`}
                    />
                    {selected && (
                      <Check className="absolute inset-0 m-auto h-4 w-4 text-gray-900" />
                    )}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Enabled */}
          <div className="flex items-center gap-2">
            <input
              id="rule-enabled"
              type="checkbox"
              checked={draft.enabled}
              onChange={(e) => setField('enabled', e.target.checked)}
              className="h-4 w-4"
            />
            <Label htmlFor="rule-enabled" className="cursor-pointer">
              Enabled
            </Label>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={onCancel}>
            Cancel
          </Button>
          <Button onClick={onSave} data-testid="save-rule">
            {isEdit ? 'Save Rule' : 'Add Rule'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ── a single condition row (field → operator → adaptive value) ──────────────

interface ConditionRowProps {
  index: number;
  condition: Condition;
  activeCategoryValues: string[];
  canRemove: boolean;
  onChange: (next: Condition) => void;
  onRemove: () => void;
}

function ConditionRow({
  index,
  condition,
  activeCategoryValues,
  canRemove,
  onChange,
  onRemove,
}: ConditionRowProps) {
  const operators = OPERATORS_BY_FIELD[condition.field];

  const handleFieldChange = (field: ConditionField) => {
    // Reset operator + value to sane defaults for the new field so the value
    // control always matches the field.
    onChange({ field, operator: OPERATORS_BY_FIELD[field][0], value: '' });
  };

  const handleOperatorChange = (operator: ConditionOperator) => {
    // Switching to/from list/range operators changes the value shape.
    let value: unknown = condition.value;
    if (operator === 'between') value = ['', ''];
    else if (operator === 'in') value = Array.isArray(condition.value) ? condition.value : [];
    else if (Array.isArray(condition.value)) value = '';
    onChange({ ...condition, operator, value });
  };

  return (
    <div
      className="grid grid-cols-1 gap-2 rounded-md border p-3 sm:grid-cols-[1fr_1fr_1.4fr_auto] sm:items-end"
      data-testid={`condition-row-${index}`}
    >
      {/* Field */}
      <div className="space-y-1">
        <Label htmlFor={`cond-field-${index}`} className="text-xs">
          Field
        </Label>
        <select
          id={`cond-field-${index}`}
          value={condition.field}
          onChange={(e) => handleFieldChange(e.target.value as ConditionField)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          {FIELD_OPTIONS.map((f) => (
            <option key={f.value} value={f.value}>
              {f.label}
            </option>
          ))}
        </select>
      </div>

      {/* Operator */}
      <div className="space-y-1">
        <Label htmlFor={`cond-operator-${index}`} className="text-xs">
          Operator
        </Label>
        <select
          id={`cond-operator-${index}`}
          value={condition.operator}
          onChange={(e) => handleOperatorChange(e.target.value as ConditionOperator)}
          className="flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm"
        >
          {OPERATOR_OPTIONS.filter((o) => operators.includes(o.value)).map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
      </div>

      {/* Adaptive value control */}
      <div className="space-y-1">
        <Label htmlFor={`cond-value-${index}`} className="text-xs">
          Value
        </Label>
        <ConditionValue
          index={index}
          condition={condition}
          activeCategoryValues={activeCategoryValues}
          onChange={onChange}
        />
      </div>

      <div className="flex justify-end">
        <Button
          variant="ghost"
          size="icon"
          onClick={onRemove}
          disabled={!canRemove}
          aria-label={`Remove condition ${index + 1}`}
          className="text-red-600 hover:bg-red-50"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// The value control ADAPTS to the field (and operator):
//  - category        → dropdown of active category values (or comma list for `in`)
//  - workflow_stage  → dropdown of known stages (or comma list for `in`)
//  - effective_fee   → numeric input(s); `between` shows min + max
function ConditionValue({
  index,
  condition,
  activeCategoryValues,
  onChange,
}: {
  index: number;
  condition: Condition;
  activeCategoryValues: string[];
  onChange: (next: Condition) => void;
}) {
  const { field, operator, value } = condition;
  const inputClass =
    'flex h-9 w-full rounded-md border border-input bg-background px-2 text-sm';

  // effective_fee — numeric.
  if (field === 'effective_fee') {
    if (operator === 'between') {
      const arr = Array.isArray(value) ? value : ['', ''];
      return (
        <div className="flex items-center gap-2">
          <Input
            id={`cond-value-${index}`}
            type="number"
            aria-label={`Minimum value for condition ${index + 1}`}
            value={(arr[0] as string | number) ?? ''}
            onChange={(e) =>
              onChange({
                ...condition,
                value: [numOrEmpty(e.target.value), arr[1] ?? ''],
              })
            }
            placeholder="Min"
          />
          <span className="text-muted-foreground">–</span>
          <Input
            type="number"
            aria-label={`Maximum value for condition ${index + 1}`}
            value={(arr[1] as string | number) ?? ''}
            onChange={(e) =>
              onChange({
                ...condition,
                value: [arr[0] ?? '', numOrEmpty(e.target.value)],
              })
            }
            placeholder="Max"
          />
        </div>
      );
    }
    return (
      <Input
        id={`cond-value-${index}`}
        type="number"
        value={(value as string | number) ?? ''}
        onChange={(e) => onChange({ ...condition, value: numOrEmpty(e.target.value) })}
        placeholder="Amount"
      />
    );
  }

  // category / workflow_stage discrete options.
  const options =
    field === 'category'
      ? activeCategoryValues.map((v) => ({ value: v, label: v }))
      : WORKFLOW_STAGE_OPTIONS;

  // `in` (set membership) — multi-select via checkboxes for discrete fields.
  if (operator === 'in') {
    const selected = Array.isArray(value) ? (value as string[]) : [];
    return (
      <div
        className="flex max-h-28 flex-wrap gap-x-3 gap-y-1 overflow-y-auto rounded-md border p-2"
        data-testid={`cond-value-multi-${index}`}
      >
        {options.map((o) => {
          const checked = selected.includes(o.value);
          return (
            <label key={o.value} className="flex items-center gap-1.5 text-sm">
              <input
                type="checkbox"
                checked={checked}
                aria-label={o.label}
                onChange={(e) =>
                  onChange({
                    ...condition,
                    value: e.target.checked
                      ? [...selected, o.value]
                      : selected.filter((v) => v !== o.value),
                  })
                }
              />
              {o.label}
            </label>
          );
        })}
      </div>
    );
  }

  // equals — single dropdown.
  return (
    <select
      id={`cond-value-${index}`}
      value={typeof value === 'string' ? value : ''}
      onChange={(e) => onChange({ ...condition, value: e.target.value })}
      className={inputClass}
    >
      <option value="">Select…</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
  );
}

/** Parse a numeric input to a number, or '' when blank (kept editable). */
function numOrEmpty(raw: string): number | '' {
  if (raw.trim() === '') return '';
  const n = Number(raw);
  return Number.isFinite(n) ? n : '';
}
